import { ComboBox, FormControl, Tooltip } from '@airbus/components-react';
import { useEffect, useMemo, useState } from 'react';
import { fieldValidator } from '../../../shared/utils/yupSchemaCreator';
import { useLanguageContextAPI } from '../../../shared/context/LanguageContext';
import {
  applyErledigtAvYesGuard,
  getErledigtAvDisabledTooltipKey,
  isErledigtAvYesEnabled,
} from '../services/erledigtAvNeedGate';
import { AllDropdowns } from '../../../shared/types';

type Props = {
  rowId: number | string;
  initialValues: Record<string, any>;
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  blurHandler: (name: string, value: unknown) => void;
  field: any;
  language: string;
  options: Array<{ label: string; value: string | number; disabled?: boolean }>;
  allDropdowns?: AllDropdowns;
  handleEnterTabBlur?: (e: React.KeyboardEvent) => void;
};

export default function ErledigtAvFormField(props: Props) {
  const { t } = useLanguageContextAPI();
  const {
    rowId,
    initialValues,
    name,
    label,
    required,
    disabled,
    blurHandler,
    field,
    language,
    options,
    allDropdowns,
    handleEnterTabBlur,
  } = props;

  const valueOptions = useMemo(
    () =>
      applyErledigtAvYesGuard({
        options: options ?? [],
        row: initialValues,
        allDropdowns,
      }),
    [options, initialValues, allDropdowns],
  );
  const canFinalize = isErledigtAvYesEnabled({ row: initialValues, allDropdowns });
  const disabledTooltip = t(getErledigtAvDisabledTooltipKey({ row: initialValues, allDropdowns }));

  const [selected, setSelected] = useState<{
    label: string;
    value: string | number;
    disabled?: boolean;
  } | null>(null);
  const [error, setError] = useState<any>(null);
  const [touched, setTouched] = useState(false);

  const getFallbackSelection = () =>
    valueOptions.find((item) => String(item.value) === String(initialValues?.[name])) ?? null;

  useEffect(() => {
    if (!initialValues) {
      return;
    }

    setSelected(getFallbackSelection());

    let isTouched = false;
    if (
      initialValues?.isCommitRow ||
      (initialValues?.status === 'E' && initialValues?.backup?.[name] !== initialValues[name]) ||
      ((initialValues?.status === 'N' || initialValues?.status === 'C') && initialValues?.touched[name])
    ) {
      isTouched = true;
    }
    setTouched(isTouched);

    if (isTouched) {
      const nextErr = fieldValidator(field, initialValues[name], language);
      setError(nextErr);
    }
  }, [initialValues, name, field, language]);

  const handleChange = (obj: any) => {
    const nextValue = obj?.value ?? null;
    const nextErr = fieldValidator(field, nextValue, language);
    setSelected(obj ?? null);
    setError(nextErr);
  };

  const commitSelected = () => {
    const nextValue = selected?.value ?? null;

    if (selected?.disabled) {
      setSelected(getFallbackSelection());
      return;
    }

    if (field?.onValueChange) {
      const valid = field.onValueChange({
        rowId,
        value: nextValue,
        row: initialValues,
      });
      if (valid === false) {
        setSelected(getFallbackSelection());
        return;
      }
    }

    blurHandler(name, nextValue);
  };

  const comboBoxElement = (
    <ComboBox
      name={name}
      disabled={disabled}
      size='small'
      options={valueOptions}
      value={selected}
      getOptionLabel={(option: any) => option.label}
      getOptionValue={(option: any) => option.value}
      invalid={touched && Boolean(error)}
      onChange={(obj: any) => handleChange(obj)}
      keepInputOnSelect={true}
      allowVirtualization={true}
      onBlur={commitSelected}
      onKeyDown={handleEnterTabBlur}
      hideClear
    />
  );

  return (
    <FormControl
      id={name}
      disabled={disabled}
      required={required}
      label={label}
      error={touched && Boolean(error)}
      errorText={touched && error?.msg}
      className='disabled-input'
    >
      {!canFinalize ? (
        <Tooltip label={disabledTooltip} placement='top' style={{ width: 'max-content' }}>
          <div>{comboBoxElement}</div>
        </Tooltip>
      ) : (
        comboBoxElement
      )}
    </FormControl>
  );
}